import { type ReactNode, type RefObject, useImperativeHandle, useRef } from "react";
import PreviewTableRow from "~/features/bulkAddToCart/components/PreviewTable/PreviewTableRow";
import { type PreviewItem, type PreviewItemUpdateData } from "~/features/bulkAddToCart/types/models";
import classNames from "classnames";
import { HAS_END_CUSTOMER } from "~/utils/constants";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import { distinct, isNonNull } from "~/common/utils/reducers";
import { userCanSeePriceReference } from "~/features/cart/cart/components/LineItemStaticParams";
import { useTranslation } from "react-i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import CommentsDisabledOutlinedIcon from "@mui/icons-material/CommentsDisabledOutlined";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { isTargetPriceEnabled } from "~/app/config/featureFlags";
import { iconClickablePrimarySx } from "~/common/components/icon/iconClickableSx";
import { PreviewTableRoot } from "~/features/bulkAddToCart/components/PreviewTable/PreviewTable.styles";
import { Stack, styled, Typography } from "~/common/mui";

export interface PreviewTableRef {
  scrollToRow: (index: number) => void;
}

interface Props {
  items: PreviewItem[];
  onDataChange: (index: number, newData: Partial<PreviewItemUpdateData>) => void;
  onRemoveItem: (index: number) => void;
  onRemoveAllItems: () => void;
  onChangeAllCommentsVisibility: (visible: boolean) => void;
  onChangeCommentVisibility: (index: number, visible: boolean) => void;
  saleOnly: boolean;
  setSaleOnly: (visible: boolean) => void;
  listRef: RefObject<PreviewTableRef>;
}

interface BulkHeaderTitleProps {
  children: ReactNode;
}

const PreviewTable = ({
  items,
  onDataChange,
  onRemoveItem,
  onRemoveAllItems,
  onChangeAllCommentsVisibility,
  onChangeCommentVisibility,
  listRef,
}: Props) => {
  const { t } = useTranslation();

  const virtualListContainerRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    estimateSize: () => 220,
    overscan: 8,
    getScrollElement: () => internalTableRef.current?.closest(".scha__modal__contents__children") ?? null,
    scrollPaddingEnd: 100,
    scrollPaddingStart: 100,
    // @ts-expect-error
    shouldAdjustScrollPositionOnItemSizeChange: () => true,
  });

  const internalTableRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(
    listRef,
    () => ({
      scrollToRow: (index) => {
        rowVirtualizer.scrollToIndex(index, { align: "start" });

        rowVirtualizer.scrollElement?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({
          block: "center",
        });
      },
    }),
    [],
  );

  const showPriceRefColumn = userCanSeePriceReference();

  const allCommentsOpen = items.every((item) => item.isCommentVisible);

  const canMakeComments = distinct(items.map((item) => item.marketType).filter(isNonNull)).some((marketType) =>
    canUseCommentsForProduct(marketType),
  );

  const showPriceReferenceColumn = showPriceRefColumn && !HAS_END_CUSTOMER;
  const targetPriceEnabled = isTargetPriceEnabled();

  const BulkHeaderTitle = ({ children }: BulkHeaderTitleProps) => {
    return (
      <Typography variant="bodyMedium" component={"b"}>
        {children}
      </Typography>
    );
  };

  return (
    <PreviewTableRoot
      className={classNames(
        "scha__bulk-preview__table",
        showPriceRefColumn && "scha__bulk-preview__table--priceRef",
        targetPriceEnabled && "scha__bulk-preview__table--targetPrice",
      )}
      ref={internalTableRef}
    >
      <BulkHeaderTitles showPriceReferenceColumn={showPriceReferenceColumn} targetPriceEnabled={targetPriceEnabled}>
        <BulkHeaderTitle>{t("wishlistPage.table.header.number")}</BulkHeaderTitle>
        <BulkHeaderTitle>{t("bulkUpload.table.header.productId")}</BulkHeaderTitle>
        <BulkHeaderTitle>{t("cartPage.table.header.quantity.title")}</BulkHeaderTitle>
        <BulkHeaderTitle>{t("cartPage.table.header.delivery.title")}</BulkHeaderTitle>
        {showPriceReferenceColumn && (
          <BulkHeaderTitle>{t("cartPage.table.header.priceReference.title")}</BulkHeaderTitle>
        )}
        {targetPriceEnabled && <BulkHeaderTitle>{t("cartPage.table.header.targetPrice.title")}</BulkHeaderTitle>}

        <Stack flexDirection={"row"} gap={1}>
          {canMakeComments &&
            (allCommentsOpen ? (
              <CommentsDisabledOutlinedIcon
                sx={iconClickablePrimarySx}
                onClick={() => onChangeAllCommentsVisibility(!allCommentsOpen)}
                fontSize="medium"
              />
            ) : (
              <CommentOutlinedIcon
                sx={iconClickablePrimarySx}
                onClick={() => onChangeAllCommentsVisibility(!allCommentsOpen)}
                fontSize="medium"
              />
            ))}
          <DeleteOutlineOutlinedIcon
            sx={{ ...iconClickablePrimarySx, ml: canMakeComments ? 0 : 4 }}
            onClick={onRemoveAllItems}
            fontSize="medium"
          />
        </Stack>
      </BulkHeaderTitles>

      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
        ref={virtualListContainerRef}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => (
          <div
            key={virtualRow.index}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            <PreviewTableRow
              key={virtualRow.index}
              index={virtualRow.index}
              data={items[virtualRow.index]}
              onDataChange={onDataChange}
              onSetCommentVisibility={onChangeCommentVisibility}
              onRemoveItem={onRemoveItem}
              showPriceReference={showPriceReferenceColumn}
              onRender={rowVirtualizer.measureElement}
              targetPriceEnabled={targetPriceEnabled}
            />
          </div>
        ))}
      </div>
    </PreviewTableRoot>
  );
};

export const BulkHeaderTitles = styled("div", {
  shouldForwardProp: (prop) => prop !== "showPriceReferenceColumn" && prop !== "targetPriceEnabled",
})<{
  showPriceReferenceColumn: boolean;
  targetPriceEnabled: boolean;
}>(({ theme, showPriceReferenceColumn, targetPriceEnabled }) => {
  return {
    display: "grid",

    gridTemplateColumns:
      showPriceReferenceColumn && targetPriceEnabled
        ? "32px 200px 136px 200px 264px 135px 56px"
        : showPriceReferenceColumn || targetPriceEnabled
        ? "32px 272px 136px 272px 270px 56px"
        : "32px 322px 322px 323px 56px",

    gap: "16px",
    alignItems: "center",
    padding: "8px 16px",
    borderBottom: `3px solid ${theme.palette?.divider}`,
  };
});

export default PreviewTable;
